import React, { useState } from 'react'
import { LIBERTY_IMAGES } from '../assets/asset'
import { useNavigate } from 'react-router-dom'
import TokenSelector from '../components/TokenSelector'
import { useWalletUI } from '../hooks/useWalletUI'
import { useSelectedTokenBalance } from '../hooks/useSelectedTokenBalance'
import { useSwapQuote } from '../hooks/useSwapQuote'
import useApproval from '../hooks/useApproval'

function normalizeDecimalInput(value) {
    const cleaned = String(value || '').replace(/[^\d.]/g, '')
    const parts = cleaned.split('.')
    const rawInt = parts[0] || ''
    const normalizedInt = rawInt.replace(/^0+(?=\d)/, '') || '0'
    if (parts.length <= 1) return normalizedInt
    return `${normalizedInt}.${parts.slice(1).join('')}`
}

const SWAP_PROGRESS_USDC_SRC =
    'https://raw.githubusercontent.com/libertyswap-finance/app-tokens/main/token-logo/0x15D38573d2feeb82e7ad5187aB8c1D52810B1f07.png'
const SWAP_PROGRESS_WPLS_SRC =
    'https://raw.githubusercontent.com/libertyswap-finance/app-tokens/main/token-logo/0xA1077a294dDE1B09bB078844df40758a5D0f9a27.png'

function SwapProgressPath() {
    const tokenIconClass =
        'relative flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#393941] bg-[#1A1A1D]'
    const tokenImgClass = 'max-h-full max-w-full object-contain object-center'
    const tokenSlot = 'flex w-12 shrink-0 justify-center'
    const connectorClass = 'h-2 w-5 shrink-0 object-contain'
    return (
        <div className="flex w-max max-w-full shrink-0 flex-col gap-1">
            <div className="flex items-center gap-2">
                <div className={tokenSlot}>
                    <div className={tokenIconClass}>
                        <img
                            alt="USDC"
                            width={24}
                            height={24}
                            decoding="async"
                            data-nimg={1}
                            className={tokenImgClass}
                            style={{ color: 'transparent' }}
                            src={SWAP_PROGRESS_USDC_SRC}
                            data-savepage-loading="lazy"
                        />
                    </div>
                </div>
                <img
                    alt=""
                    width={20}
                    height={8}
                    decoding="async"
                    data-nimg={1}
                    className={connectorClass}
                    style={{ color: 'transparent' }}
                    src="/exchange/step-connector.svg"
                    data-savepage-loading="lazy"
                />
                <div className={tokenSlot}>
                    <div className={tokenIconClass}>
                        <img
                            alt="WPLS"
                            width={24}
                            height={24}
                            decoding="async"
                            data-nimg={1}
                            className={tokenImgClass}
                            style={{ color: 'transparent' }}
                            src={SWAP_PROGRESS_WPLS_SRC}
                            data-savepage-loading="lazy"
                        />
                    </div>
                </div>
                <img
                    alt=""
                    width={20}
                    height={8}
                    decoding="async"
                    data-nimg={1}
                    className={connectorClass}
                    style={{ color: 'transparent' }}
                    src="/exchange/step-connector.svg"
                    data-savepage-loading="lazy"
                />
                <div className={tokenSlot}>
                    <div className={tokenIconClass}>
                        <img
                            alt="PLS"
                            width={24}
                            height={24}
                            decoding="async"
                            data-nimg={1}
                            className={tokenImgClass}
                            style={{ color: 'transparent' }}
                            src={SWAP_PROGRESS_WPLS_SRC}
                            data-savepage-loading="lazy"
                        />
                    </div>
                </div>
            </div>
            <div className="flex items-start gap-2">
                <div className={`${tokenSlot} min-w-0 text-center text-white tracking-wide`}>USDC</div>
                <div className="w-5 shrink-0" aria-hidden="true" />
                <div className={`${tokenSlot} min-w-0 text-center text-white tracking-wide`}>WPLS</div>
                <div className="w-5 shrink-0" aria-hidden="true" />
                <div className={`${tokenSlot} min-w-0 text-center text-white tracking-wide`}>PLS</div>
            </div>
        </div>
    )
}

const X_SWAP_PAY_INIT = {
    symbol: 'USDC',
    chain: 'PulseChain',
    chainId: 369,
    address: '0x15d38573d2feeb82e7ad5187ab8c1d52810b1f07',
    decimals: 6,
    image: SWAP_PROGRESS_USDC_SRC,
    chainBadge: LIBERTY_IMAGES.PULSECHAIN,
}

const X_SWAP_RECEIVE_INIT = {
    symbol: 'PLS',
    chain: 'PulseChain',
    chainId: 369,
    address: '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee',
    decimals: 18,
    image: SWAP_PROGRESS_WPLS_SRC,
    chainBadge: LIBERTY_IMAGES.PULSECHAIN,
}

function XTokenPill({ token, onOpen }) {
    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onOpen}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onOpen()
                }
            }}
            className="inline-flex flex-col items-center justify-between bg-[#242429] py-1.5 md:py-1 px-2 rounded-full cursor-pointer hover:bg-[#333333]
  font-medium whitespace-nowrap self-start undefined"
        >
            <div className="flex items-center gap-2 w-full">
                <div className="flex items-center">
                    <div className="relative mr-2">
                        <img
                            alt={token.symbol}
                            width={32}
                            height={32}
                            decoding="async"
                            data-nimg={1}
                            className="border border-[#393941] rounded-full"
                            style={{ color: 'transparent' }}
                            src={token.image}
                            data-savepage-loading="lazy"
                        />
                        <div className="absolute -bottom-1 -right-1">
                            <img
                                alt={token.chain}
                                width={16}
                                height={16}
                                decoding="async"
                                data-nimg={1}
                                className="border border-[#393941] rounded-full"
                                style={{ color: 'transparent' }}
                                src={token.chainBadge}
                                data-savepage-loading="lazy"
                            />
                        </div>
                    </div>
                    <div className="flex flex-col items-start md:mr-2">
                        <span className="text-[#F7F7F8] font-medium text-base">{token.symbol}</span>
                        <span className="text-[#9091A0] text-xs md:block hidden">{token.chain}</span>
                    </div>
                    <svg className="w-5 h-5 text-neutral-400" viewBox="0 0 20 20" fill="currentColor">
                        <path
                            fillRule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clipRule="evenodd"
                        />
                    </svg>
                </div>
            </div>
        </div>
    )
}

const X = () => {
    const navigate = useNavigate()
    const [tokenSelectorOpen, setTokenSelectorOpen] = useState(false)
    const [tokenPickerField, setTokenPickerField] = useState('pay')
    const [payToken, setPayToken] = useState(X_SWAP_PAY_INIT)
    const [receiveToken, setReceiveToken] = useState(X_SWAP_RECEIVE_INIT)
    const [payAmount, setPayAmount] = useState('0')
    const { address, isConnected, openConnectModal } = useWalletUI()
    const { approveContract, isApproveLoading } = useApproval()
    const { balance: payBalance, loading: payBalanceLoading, refresh: refreshPayBalance } = useSelectedTokenBalance(
        payToken,
        address
    )
    const { receiveAmount, rateText, loading: quoteLoading } = useSwapQuote({
        payToken,
        receiveToken,
        payAmount,
    })

    const openPayTokenPicker = () => {
        setTokenPickerField('pay')
        setTokenSelectorOpen(true)
    }
    const openReceiveTokenPicker = () => {
        setTokenPickerField('receive')
        setTokenSelectorOpen(true)
    }
    return (
        <>
            <main className="pt-0 md:pt-20">
                <div className="md:min-h-[calc(100vh-72px)] flex flex-col items-center justify-center py-4 md:py-6">
                    <div className="flex w-full max-w-full flex-col items-center px-0 md:px-6">
                        <div className="relative mx-auto hidden w-full max-w-[1000px] md:flex md:flex-col md:items-center">
                            <img
                                alt=""
                                decoding="async"
                                data-nimg="fill"
                                className="object-cover pointer-events-none z-0"
                                style={{
                                    position: "absolute",
                                    height: "100%",
                                    width: "100%",
                                    left: 0,
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    color: "transparent"
                                }}
                                src={LIBERTY_IMAGES.EXCHANGE_BACKDROP}
                                data-savepage-loading="lazy"
                            />
                            <div className="relative mx-auto w-full max-w-[600px] rounded-3xl">
                                <div
                                    className="absolute inset-0 rounded-3xl"
                                    style={{
                                        background:
                                            "conic-gradient(from -39deg at 50% 50%, #0091FF 0deg, #FF02F0 130.2588415145874deg, #ED5F00 187.19999313354492deg, #B38CFF 293.56683254241943deg)",
                                        filter: "blur(20px)"
                                    }}
                                    aria-hidden="true"
                                />
                                <div className="relative z-10 w-full max-w-[600px]">
                                    <div
                                        className="pt-4 md:pt-6 pr-4 md:pr-6 pl-4 md:pl-6 w-full min-w-[320px] max-w-[600px] overflow-hidden rounded-3xl bg-[#0F0F0F] shadow-[0_0_40px_rgba(0,145,255,0.15),0_0_80px_rgba(179,140,255,0.08)]"
                                        style={{
                                            backgroundImage: `url(${LIBERTY_IMAGES.EXCHANGE_GRADIENT_BG})`,
                                            backgroundSize: "100% auto",
                                            backgroundPosition: "top center",
                                            backgroundRepeat: "no-repeat"
                                        }}
                                    >
                                        <div className="flex justify-between items-center text-center">
                                            <span className="text-2xl font-soloist">LIBERTYX</span>
                                        </div>
                                        <span className="text-xs italic">Powered by LibertySwap</span>
                                        <div className="mt-2" />
                                        <div className="hidden md:block">
                                            <div className="flex p-3 pb-10 -mb-7 rounded-xl bg-[#0F0F0F] undefined">
                                                <div className="flex items-center gap-1 rounded-full bg-[#18181B] border border-[#242429] p-[2px]">
                                                    <button onClick={() => navigate('/x')} className="rounded-xl font-medium transition-all duration-300 cursor-pointer text-white hover:opacity-90 overflow-hidden   rounded-full!">
                                                        <div>
                                                            <div
                                                                className="flex items-center justify-center px-4 py-1 text-xs "
                                                                style={{ background: "var(--gradient-7)" }}
                                                            >
                                                                <div>Swap</div>
                                                            </div>
                                                        </div>
                                                    </button>
                                                    <button onClick={() => navigate('/xLimit')} className="rounded-xl font-medium transition-all duration-300 cursor-pointer text-white hover:opacity-90 overflow-hidden   rounded-full!">
                                                        <div>
                                                            <div
                                                                className="flex items-center justify-center px-4 py-1 text-xs "
                                                                style={{
                                                                    backgroundColor: "transparent",
                                                                    textColor: "#9091A0"
                                                                }}
                                                            >
                                                                <div>Limit</div>
                                                            </div>
                                                        </div>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="relative space-y-2">
                                                <div className="token-input bg-[#18181B] rounded-xl p-4 w-full">
                                                    <div className="flex justify-between items-center mb-2">
                                                        <span className="text-gray-400 pt-0.5 antialiased text-xs md:text-sm">
                                                            You pay
                                                        </span>
                                                        <div className="flex flex-row items-center">
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    className="text-xs md:text-sm cursor-pointer bg-[#393941] hover:bg-[#333333] text-gray-300 px-2 py-0.5 rounded"
                                                                    onClick={() => setPayAmount(isConnected ? (payBalance || '0') : '0')}
                                                                >
                                                                    Max
                                                                </button>
                                                                <span className="text-gray-400 text-xs md:text-sm flex items-center">
                                                                    Balance: {isConnected && payBalanceLoading ? '...' : (isConnected ? payBalance : '0')} {payToken.symbol}
                                                                    <span
                                                                        className="ml-1.5 cursor-pointer hover:opacity-70"
                                                                        title="Refresh balance"
                                                                        onClick={refreshPayBalance}
                                                                    >
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            width={20}
                                                                            height={20}
                                                                            viewBox="0 0 24 24"
                                                                            fill="none"
                                                                        >
                                                                            <path
                                                                                fill="#ffffff"
                                                                                d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"
                                                                                opacity=".4"
                                                                            />
                                                                            <path
                                                                                fill="#ffffff"
                                                                                d="M16.759 11.93a.741.741 0 00-.85.64c-.12.87-.51 1.65-1.11 2.25-1.38 1.38-3.52 1.52-5.07.45h.75c.41 0 .75-.34.75-.75s-.34-.75-.75-.75h-2.67c-.41 0-.75.34-.75.75v2.67c0 .41.34.75.75.75s.75-.34.75-.75v-.94c1 .81 2.21 1.24 3.43 1.24 1.4 0 2.8-.53 3.87-1.6.83-.83 1.36-1.9 1.53-3.1a.734.734 0 00-.63-.86zM7.34 12.08c.37 0 .69-.27.74-.64.12-.87.51-1.65 1.11-2.25 1.38-1.38 3.52-1.52 5.07-.45h-.75c-.41 0-.75.34-.75.75s.34.75.75.75h2.67c.41 0 .75-.34.75-.75V6.82c0-.41-.34-.75-.75-.75s-.75.34-.75.75v.94c-2.15-1.74-5.3-1.63-7.29.36-.83.83-1.36 1.9-1.53 3.1-.06.41.23.79.64.85.02.01.06.01.09.01z"
                                                                            />
                                                                        </svg>
                                                                    </span>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <div className="relative flex-1">
                                                            <div className="relative w-full">
                                                                <input
                                                                    type="text"
                                                                    inputMode="decimal"
                                                                    pattern="[0-9]*[.,]?[0-9]*"
                                                                    placeholder={0}
                                                                    style={{ fontSize: 24 }}
                                                                    className="bg-transparent outline-none w-full font-semibold  pr-4"
                                                                    value={payAmount}
                                                                    onChange={(e) => setPayAmount(normalizeDecimalInput(e.target.value))}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <XTokenPill token={payToken} onOpen={openPayTokenPicker} />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex justify-center -mt-5 -mb-5">
                                                    <button>
                                                        <div className="border border-[#41424B] p-[7px] rounded-xl transition-all duration-200 bg-[#18181B]">
                                                            <img
                                                                alt="Swap"
                                                                width={24}
                                                                height={24}
                                                                decoding="async"
                                                                data-nimg={1}
                                                                style={{ color: "transparent" }}
                                                                src={LIBERTY_IMAGES.IC_SWAP_ARROW_UP_DOWN}
                                                                data-savepage-loading="lazy"
                                                            />
                                                        </div>
                                                    </button>
                                                </div>
                                                <div className="token-input bg-[#18181B] rounded-xl p-4 w-full">
                                                    <div className="flex justify-between items-center mb-2">
                                                        <span className="text-gray-400 pt-0.5 antialiased text-xs md:text-sm">
                                                            You receive
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <div className="relative flex-1">
                                                            <div className="relative w-full">
                                                                <input
                                                                    type="text"
                                                                    inputMode="decimal"
                                                                    pattern="[0-9]*[.,]?[0-9]*"
                                                                    placeholder={0}
                                                                    style={{ fontSize: 24 }}
                                                                    className={`bg-transparent outline-none w-full font-semibold pr-4 ${quoteLoading ? 'text-transparent bg-white/10 rounded-md animate-pulse' : ''}`}
                                                                    value={quoteLoading ? ' ' : receiveAmount}
                                                                    readOnly
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <XTokenPill token={receiveToken} onOpen={openReceiveTokenPicker} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="bg-[#18181B] rounded-xl p-4 space-y-3 text-sm mt-4">
                                                <div className="w-full rounded-xl p-3 bg-[#242429] flex justify-between ">
                                                    <div className="text-[#9091A0] text-md">Progress</div>
                                                    <SwapProgressPath />
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-gray-400">Rate</span>
                                                    {quoteLoading ? (
                                                        <span className="inline-block h-4 w-24 rounded bg-white/15 animate-pulse" />
                                                    ) : (
                                                        <span className="text-white">{rateText}</span>
                                                    )}
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-gray-400">Estimated time</span>
                                                    <span className="text-white">2-3 min</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="md:hidden">
                                            <div className="relative z-0">
                                                <div
                                                    className="absolute inset-0 rounded-3xl z-0"
                                                    style={{
                                                        background:
                                                            "conic-gradient(from -39deg at 50% 50%, #0091FF 0deg, #FF02F0 130.2588415145874deg, #ED5F00 187.19999313354492deg, #B38CFF 293.56683254241943deg)",
                                                        filter: "blur(30px)"
                                                    }}
                                                    aria-hidden="true"
                                                />
                                                <div className="relative z-10">
                                                    <div className="flex p-3 pb-10 -mb-7 rounded-xl bg-[#0F0F0F] undefined">
                                                        <div className="flex items-center gap-1 rounded-full bg-[#18181B] border border-[#242429] p-[2px]">
                                                            <button className="rounded-xl font-medium transition-all duration-300 cursor-pointer text-white hover:opacity-90 overflow-hidden   rounded-full!">
                                                                <div>
                                                                    <div
                                                                        className="flex items-center justify-center px-4 py-1 text-xs "
                                                                        style={{ background: "var(--gradient-7)" }}
                                                                    >
                                                                        <div>Swap</div>
                                                                    </div>
                                                                </div>
                                                            </button>
                                                            <button className="rounded-xl font-medium transition-all duration-300 cursor-pointer text-white hover:opacity-90 overflow-hidden   rounded-full!">
                                                                <div>
                                                                    <div
                                                                        className="flex items-center justify-center px-4 py-1 text-xs "
                                                                        style={{
                                                                            backgroundColor: "transparent",
                                                                            textColor: "#9091A0"
                                                                        }}
                                                                    >
                                                                        <div>Limit</div>
                                                                    </div>
                                                                </div>
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="relative space-y-2">
                                                        <div className="token-input bg-[#18181B] rounded-xl p-4 w-full">
                                                            <div className="flex justify-between items-center mb-2">
                                                                <span className="text-gray-400 pt-0.5 antialiased text-xs md:text-sm">
                                                                    You pay
                                                                </span>
                                                                <div className="flex flex-row items-center">
                                                                    <div className="flex items-center gap-2">
                                                                        <button
                                                                            className="text-xs md:text-sm cursor-pointer bg-[#393941] hover:bg-[#333333] text-gray-300 px-2 py-0.5 rounded"
                                                                            onClick={() => setPayAmount(isConnected ? (payBalance || '0') : '0')}
                                                                        >
                                                                            Max
                                                                        </button>
                                                                        <span className="text-gray-400 text-xs md:text-sm flex items-center">
                                                                            Balance: {isConnected && payBalanceLoading ? '...' : (isConnected ? payBalance : '0')} {payToken.symbol}
                                                                            <span
                                                                                className="ml-1.5 cursor-pointer hover:opacity-70"
                                                                                title="Refresh balance"
                                                                                onClick={refreshPayBalance}
                                                                            >
                                                                                <svg
                                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                                    width={20}
                                                                                    height={20}
                                                                                    viewBox="0 0 24 24"
                                                                                    fill="none"
                                                                                >
                                                                                    <path
                                                                                        fill="#ffffff"
                                                                                        d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"
                                                                                        opacity=".4"
                                                                                    />
                                                                                    <path
                                                                                        fill="#ffffff"
                                                                                        d="M16.759 11.93a.741.741 0 00-.85.64c-.12.87-.51 1.65-1.11 2.25-1.38 1.38-3.52 1.52-5.07.45h.75c.41 0 .75-.34.75-.75s-.34-.75-.75-.75h-2.67c-.41 0-.75.34-.75.75v2.67c0 .41.34.75.75.75s.75-.34.75-.75v-.94c1 .81 2.21 1.24 3.43 1.24 1.4 0 2.8-.53 3.87-1.6.83-.83 1.36-1.9 1.53-3.1a.734.734 0 00-.63-.86zM7.34 12.08c.37 0 .69-.27.74-.64.12-.87.51-1.65 1.11-2.25 1.38-1.38 3.52-1.52 5.07-.45h-.75c-.41 0-.75.34-.75.75s.34.75.75.75h2.67c.41 0 .75-.34.75-.75V6.82c0-.41-.34-.75-.75-.75s-.75.34-.75.75v.94c-2.15-1.74-5.3-1.63-7.29.36-.83.83-1.36 1.9-1.53 3.1-.06.41.23.79.64.85.02.01.06.01.09.01z"
                                                                                    />
                                                                                </svg>
                                                                            </span>
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-4">
                                                                <div className="relative flex-1">
                                                                    <div className="relative w-full">
                                                                        <input
                                                                            type="text"
                                                                            inputMode="decimal"
                                                                            pattern="[0-9]*[.,]?[0-9]*"
                                                                            placeholder={0}
                                                                            style={{ fontSize: 24 }}
                                                                            className="bg-transparent outline-none w-full font-semibold  pr-4"
                                                                            value={payAmount}
                                                                            onChange={(e) => setPayAmount(normalizeDecimalInput(e.target.value))}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <XTokenPill token={payToken} onOpen={openPayTokenPicker} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex justify-center -mt-5 -mb-5">
                                                            <button>
                                                                <div className="border border-[#41424B] p-[7px] rounded-xl transition-all duration-200 bg-[#18181B]">
                                                                    <img
                                                                        alt="Swap"
                                                                        width={24}
                                                                        height={24}
                                                                        decoding="async"
                                                                        data-nimg={1}
                                                                        style={{ color: "transparent" }}
                                                                        src={LIBERTY_IMAGES.IC_SWAP_ARROW_UP_DOWN}
                                                                        data-savepage-loading="lazy"
                                                                    />
                                                                </div>
                                                            </button>
                                                        </div>
                                                        <div className="token-input bg-[#18181B] rounded-xl p-4 w-full">
                                                            <div className="flex justify-between items-center mb-2">
                                                                <span className="text-gray-400 pt-0.5 antialiased text-xs md:text-sm">
                                                                    You receive
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-4">
                                                                <div className="relative flex-1">
                                                                    <div className="relative w-full">
                                                                        <input
                                                                            type="text"
                                                                            inputMode="decimal"
                                                                            pattern="[0-9]*[.,]?[0-9]*"
                                                                            placeholder={0}
                                                                            style={{ fontSize: 24 }}
                                                                            className={`bg-transparent outline-none w-full font-semibold pr-4 ${quoteLoading ? 'text-transparent bg-white/10 rounded-md animate-pulse' : ''}`}
                                                                            value={quoteLoading ? ' ' : receiveAmount}
                                                                            readOnly
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <XTokenPill token={receiveToken} onOpen={openReceiveTokenPicker} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="bg-[#18181B] rounded-xl p-4 space-y-3 text-sm mt-4">
                                                        <div className="w-full rounded-xl p-3 bg-[#242429] flex justify-between ">
                                                            <div className="text-[#9091A0] text-md">Progress</div>
                                                            <SwapProgressPath />
                                                        </div>
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-gray-400">Rate</span>
                                                            {quoteLoading ? (
                                                                <span className="inline-block h-4 w-24 rounded bg-white/15 animate-pulse" />
                                                            ) : (
                                                                <span className="text-white">{rateText}</span>
                                                            )}
                                                        </div>
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-gray-400">Estimated time</span>
                                                            <span className="text-white">2-3 min</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="relative z-10 mt-6">
                                            <button
                                                className="rounded-xl font-medium transition-all duration-300 cursor-pointer text-white hover:opacity-90 overflow-hidden w-full  "
                                                type="button"
                                                onClick={() => {
                                                    if (!isConnected) {
                                                        openConnectModal?.()
                                                        return
                                                    }
                                                    approveContract()
                                                }}
                                                disabled={isConnected && isApproveLoading}
                                            >
                                                <div>
                                                    <div
                                                        className="flex items-center justify-center px-6 h-10 text-base "
                                                        style={{ background: "var(--gradient-7)" }}
                                                    >
                                                        <div>{isConnected ? (isApproveLoading ? 'Processing...' : 'Swap') : 'Connect Wallet'}</div>
                                                    </div>

                                                </div>
                                            </button>
                                        </div>
                                        <div className="relative z-10">
                                            <div className="mt-4">
                                                <div className="flex items-center justify-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity duration-200">
                                                    <img
                                                        alt="Coffee"
                                                        width={20}
                                                        height={20}
                                                        decoding="async"
                                                        data-nimg={1}
                                                        style={{ color: "transparent" }}
                                                        src={LIBERTY_IMAGES.IC_HEART_CUM}
                                                        data-savepage-loading="lazy"
                                                    />
                                                    <span className="text-[#F5A425] font-medium text-sm">
                                                        Buy us a coffee
                                                    </span>
                                                    <img
                                                        alt="Arrow"
                                                        width={21}
                                                        height={20}
                                                        decoding="async"
                                                        data-nimg={1}
                                                        style={{ color: "transparent" }}
                                                        src={LIBERTY_IMAGES.IC_ARROW_UP_RIGHT_1}
                                                        data-savepage-loading="lazy"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-4" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="md:hidden relative mt-12">
                            <div
                                className="fixed top-12 left-0 right-0 w-full aspect-[375/392] z-0 md:top-20"
                                style={{
                                    backgroundImage: 'url("/exchange/exchange-bg-mobile.svg")',
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    backgroundRepeat: "no-repeat",
                                    pointerEvents: "none"
                                }}
                                aria-hidden="true"
                            />
                            <div className="relative z-10 px-4 pt-3">
                                <div className="flex justify-between items-center text-center">
                                    <span className="text-2xl font-soloist">LIBERTYX</span>
                                </div>
                                <span className="text-xs italic">Powered by LibertySwap</span>
                                <div className="mt-2" />
                                <div className="hidden md:block">
                                    <div className="flex p-3 pb-10 -mb-7 rounded-xl bg-[#0F0F0F] undefined">
                                        <div className="flex items-center gap-1 rounded-full bg-[#18181B] border border-[#242429] p-[2px]">
                                            <button className="rounded-xl font-medium transition-all duration-300 cursor-pointer text-white hover:opacity-90 overflow-hidden   rounded-full!">
                                                <div>
                                                    <div
                                                        className="flex items-center justify-center px-4 py-1 text-xs "
                                                        style={{ background: "var(--gradient-7)" }}
                                                    >
                                                        <div>Swap</div>
                                                    </div>
                                                </div>
                                            </button>
                                            <button className="rounded-xl font-medium transition-all duration-300 cursor-pointer text-white hover:opacity-90 overflow-hidden   rounded-full!">
                                                <div>
                                                    <div
                                                        className="flex items-center justify-center px-4 py-1 text-xs "
                                                        style={{
                                                            backgroundColor: "transparent",
                                                            textColor: "#9091A0"
                                                        }}
                                                    >
                                                        <div>Limit</div>
                                                    </div>
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="relative space-y-2">
                                        <div className="token-input bg-[#18181B] rounded-xl p-4 w-full">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-gray-400 pt-0.5 antialiased text-xs md:text-sm">
                                                    You pay
                                                </span>
                                                <div className="flex flex-row items-center">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            className="text-xs md:text-sm cursor-pointer bg-[#393941] hover:bg-[#333333] text-gray-300 px-2 py-0.5 rounded"
                                                            onClick={() => setPayAmount(isConnected ? (payBalance || '0') : '0')}
                                                        >
                                                            Max
                                                        </button>
                                                        <span className="text-gray-400 text-xs md:text-sm flex items-center">
                                                            Balance: {isConnected && payBalanceLoading ? '...' : (isConnected ? payBalance : '0')} {payToken.symbol}
                                                            <span
                                                                className="ml-1.5 cursor-pointer hover:opacity-70"
                                                                title="Refresh balance"
                                                                onClick={refreshPayBalance}
                                                            >
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width={20}
                                                                    height={20}
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                >
                                                                    <path
                                                                        fill="#ffffff"
                                                                        d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"
                                                                        opacity=".4"
                                                                    />
                                                                    <path
                                                                        fill="#ffffff"
                                                                        d="M16.759 11.93a.741.741 0 00-.85.64c-.12.87-.51 1.65-1.11 2.25-1.38 1.38-3.52 1.52-5.07.45h.75c.41 0 .75-.34.75-.75s-.34-.75-.75-.75h-2.67c-.41 0-.75.34-.75.75v2.67c0 .41.34.75.75.75s.75-.34.75-.75v-.94c1 .81 2.21 1.24 3.43 1.24 1.4 0 2.8-.53 3.87-1.6.83-.83 1.36-1.9 1.53-3.1a.734.734 0 00-.63-.86zM7.34 12.08c.37 0 .69-.27.74-.64.12-.87.51-1.65 1.11-2.25 1.38-1.38 3.52-1.52 5.07-.45h-.75c-.41 0-.75.34-.75.75s.34.75.75.75h2.67c.41 0 .75-.34.75-.75V6.82c0-.41-.34-.75-.75-.75s-.75.34-.75.75v.94c-2.15-1.74-5.3-1.63-7.29.36-.83.83-1.36 1.9-1.53 3.1-.06.41.23.79.64.85.02.01.06.01.09.01z"
                                                                    />
                                                                </svg>
                                                            </span>
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="relative flex-1">
                                                    <div className="relative w-full">
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            pattern="[0-9]*[.,]?[0-9]*"
                                                            placeholder={0}
                                                            style={{ fontSize: 24 }}
                                                            className="bg-transparent outline-none w-full font-semibold  pr-4"
                                                            value={payAmount}
                                                            onChange={(e) => setPayAmount(normalizeDecimalInput(e.target.value))}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <XTokenPill token={payToken} onOpen={openPayTokenPicker} />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex justify-center -mt-5 -mb-5">
                                            <button>
                                                <div className="border border-[#41424B] p-[7px] rounded-xl transition-all duration-200 bg-[#18181B]">
                                                    <img
                                                        alt="Swap"
                                                        width={24}
                                                        height={24}
                                                        decoding="async"
                                                        data-nimg={1}
                                                        style={{ color: "transparent" }}
                                                        src={LIBERTY_IMAGES.IC_SWAP_ARROW_UP_DOWN}
                                                        data-savepage-loading="lazy"
                                                    />
                                                </div>
                                            </button>
                                        </div>
                                        <div className="token-input bg-[#18181B] rounded-xl p-4 w-full">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-gray-400 pt-0.5 antialiased text-xs md:text-sm">
                                                    You receive
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="relative flex-1">
                                                    <div className="relative w-full">
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            pattern="[0-9]*[.,]?[0-9]*"
                                                            placeholder={0}
                                                            style={{ fontSize: 24 }}
                                                            className={`bg-transparent outline-none w-full font-semibold pr-4 ${quoteLoading ? 'text-transparent bg-white/10 rounded-md animate-pulse' : ''}`}
                                                            value={quoteLoading ? ' ' : receiveAmount}
                                                            readOnly
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <XTokenPill token={receiveToken} onOpen={openReceiveTokenPicker} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-[#18181B] rounded-xl p-4 space-y-3 text-sm mt-4">
                                        <div className="w-full rounded-xl p-3 bg-[#242429] flex justify-between ">
                                            <div className="text-[#9091A0] text-md">Progress</div>
                                            <SwapProgressPath />
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-400">Rate</span>
                                            {quoteLoading ? (
                                                <span className="inline-block h-4 w-24 rounded bg-white/15 animate-pulse" />
                                            ) : (
                                                <span className="text-white">{rateText}</span>
                                            )}
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-400">Estimated time</span>
                                            <span className="text-white">2-3 min</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="md:hidden">
                                    <div className="relative z-0">
                                        <div
                                            className="absolute inset-0 rounded-3xl z-0"
                                            style={{
                                                background:
                                                    "conic-gradient(from -39deg at 50% 50%, #0091FF 0deg, #FF02F0 130.2588415145874deg, #ED5F00 187.19999313354492deg, #B38CFF 293.56683254241943deg)",
                                                filter: "blur(30px)"
                                            }}
                                            aria-hidden="true"
                                        />
                                        <div className="relative z-10">
                                            <div className="flex p-3 pb-10 -mb-7 rounded-xl bg-[#0F0F0F] undefined">
                                                <div className="flex items-center gap-1 rounded-full bg-[#18181B] border border-[#242429] p-[2px]">
                                                    <button className="rounded-xl font-medium transition-all duration-300 cursor-pointer text-white hover:opacity-90 overflow-hidden   rounded-full!">
                                                        <div>
                                                            <div
                                                                className="flex items-center justify-center px-4 py-1 text-xs "
                                                                style={{ background: "var(--gradient-7)" }}
                                                            >
                                                                <div>Swap</div>
                                                            </div>
                                                        </div>
                                                    </button>
                                                    <button className="rounded-xl font-medium transition-all duration-300 cursor-pointer text-white hover:opacity-90 overflow-hidden   rounded-full!">
                                                        <div>
                                                            <div
                                                                className="flex items-center justify-center px-4 py-1 text-xs "
                                                                style={{
                                                                    backgroundColor: "transparent",
                                                                    textColor: "#9091A0"
                                                                }}
                                                            >
                                                                <div>Limit</div>
                                                            </div>
                                                        </div>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="relative space-y-2">
                                                <div className="token-input bg-[#18181B] rounded-xl p-4 w-full">
                                                    <div className="flex justify-between items-center mb-2">
                                                        <span className="text-gray-400 pt-0.5 antialiased text-xs md:text-sm">
                                                            You pay
                                                        </span>
                                                        <div className="flex flex-row items-center">
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    className="text-xs md:text-sm cursor-pointer bg-[#393941] hover:bg-[#333333] text-gray-300 px-2 py-0.5 rounded"
                                                                    onClick={() => setPayAmount(isConnected ? (payBalance || '0') : '0')}
                                                                >
                                                                    Max
                                                                </button>
                                                                <span className="text-gray-400 text-xs md:text-sm flex items-center">
                                                                    Balance: {isConnected && payBalanceLoading ? '...' : (isConnected ? payBalance : '0')} {payToken.symbol}
                                                                    <span
                                                                        className="ml-1.5 cursor-pointer hover:opacity-70"
                                                                        title="Refresh balance"
                                                                        onClick={refreshPayBalance}
                                                                    >
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            width={20}
                                                                            height={20}
                                                                            viewBox="0 0 24 24"
                                                                            fill="none"
                                                                        >
                                                                            <path
                                                                                fill="#ffffff"
                                                                                d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"
                                                                                opacity=".4"
                                                                            />
                                                                            <path
                                                                                fill="#ffffff"
                                                                                d="M16.759 11.93a.741.741 0 00-.85.64c-.12.87-.51 1.65-1.11 2.25-1.38 1.38-3.52 1.52-5.07.45h.75c.41 0 .75-.34.75-.75s-.34-.75-.75-.75h-2.67c-.41 0-.75.34-.75.75v2.67c0 .41.34.75.75.75s.75-.34.75-.75v-.94c1 .81 2.21 1.24 3.43 1.24 1.4 0 2.8-.53 3.87-1.6.83-.83 1.36-1.9 1.53-3.1a.734.734 0 00-.63-.86zM7.34 12.08c.37 0 .69-.27.74-.64.12-.87.51-1.65 1.11-2.25 1.38-1.38 3.52-1.52 5.07-.45h-.75c-.41 0-.75.34-.75.75s.34.75.75.75h2.67c.41 0 .75-.34.75-.75V6.82c0-.41-.34-.75-.75-.75s-.75.34-.75.75v.94c-2.15-1.74-5.3-1.63-7.29.36-.83.83-1.36 1.9-1.53 3.1-.06.41.23.79.64.85.02.01.06.01.09.01z"
                                                                            />
                                                                        </svg>
                                                                    </span>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <div className="relative flex-1">
                                                            <div className="relative w-full">
                                                                <input
                                                                    type="text"
                                                                    inputMode="decimal"
                                                                    pattern="[0-9]*[.,]?[0-9]*"
                                                                    placeholder={0}
                                                                    style={{ fontSize: 24 }}
                                                                    className="bg-transparent outline-none w-full font-semibold  pr-4"
                                                                    value={payAmount}
                                                                    onChange={(e) => setPayAmount(normalizeDecimalInput(e.target.value))}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <XTokenPill token={payToken} onOpen={openPayTokenPicker} />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex justify-center -mt-5 -mb-5">
                                                    <button>
                                                        <div className="border border-[#41424B] p-[7px] rounded-xl transition-all duration-200 bg-[#18181B]">
                                                            <img
                                                                alt="Swap"
                                                                width={24}
                                                                height={24}
                                                                decoding="async"
                                                                data-nimg={1}
                                                                style={{ color: "transparent" }}
                                                                src={LIBERTY_IMAGES.IC_SWAP_ARROW_UP_DOWN}
                                                                data-savepage-loading="lazy"
                                                            />
                                                        </div>
                                                    </button>
                                                </div>
                                                <div className="token-input bg-[#18181B] rounded-xl p-4 w-full">
                                                    <div className="flex justify-between items-center mb-2">
                                                        <span className="text-gray-400 pt-0.5 antialiased text-xs md:text-sm">
                                                            You receive
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <div className="relative flex-1">
                                                            <div className="relative w-full">
                                                                <input
                                                                    type="text"
                                                                    inputMode="decimal"
                                                                    pattern="[0-9]*[.,]?[0-9]*"
                                                                    placeholder={0}
                                                                    style={{ fontSize: 24 }}
                                                                    className={`bg-transparent outline-none w-full font-semibold pr-4 ${quoteLoading ? 'text-transparent bg-white/10 rounded-md animate-pulse' : ''}`}
                                                                    value={quoteLoading ? ' ' : receiveAmount}
                                                                    readOnly
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <XTokenPill token={receiveToken} onOpen={openReceiveTokenPicker} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="bg-[#18181B] rounded-xl p-4 space-y-3 text-sm mt-4">
                                                <div className="w-full rounded-xl p-3 bg-[#242429] flex justify-between ">
                                                    <div className="text-[#9091A0] text-md">Progress</div>
                                                    <SwapProgressPath />
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-gray-400">Rate</span>
                                                    {quoteLoading ? (
                                                        <span className="inline-block h-4 w-24 rounded bg-white/15 animate-pulse" />
                                                    ) : (
                                                        <span className="text-white">{rateText}</span>
                                                    )}
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-gray-400">Estimated time</span>
                                                    <span className="text-white">2-3 min</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="relative z-10 mt-6">
                                    <button
                                        className="rounded-xl font-medium transition-all duration-300 cursor-pointer text-white hover:opacity-90 overflow-hidden w-full  "
                                        type="button"
                                        onClick={() => {
                                            if (!isConnected) {
                                                openConnectModal?.()
                                                return
                                            }
                                            approveContract()
                                        }}
                                        disabled={isConnected && isApproveLoading}
                                    >
                                        <div>
                                            <div
                                                className="flex items-center justify-center px-6 h-10 text-base "
                                                style={{ background: "var(--gradient-7)" }}
                                            >
                                                <div>{isConnected ? (isApproveLoading ? 'Processing...' : 'Swap') : 'Connect Wallet'}</div>
                                            </div>
                                        </div>
                                    </button>
                                </div>
                                <div className="relative z-10">
                                    <div className="mt-4">
                                        <div className="flex items-center justify-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity duration-200">
                                            <img
                                                alt="Coffee"
                                                width={20}
                                                height={20}
                                                decoding="async"
                                                data-nimg={1}
                                                style={{ color: "transparent" }}
                                                src={LIBERTY_IMAGES.IC_HEART_CUM}
                                                data-savepage-loading="lazy"
                                            />
                                            <span className="text-[#F5A425] font-medium text-sm">
                                                Buy us a coffee
                                            </span>
                                            <img
                                                alt="Arrow"
                                                width={21}
                                                height={20}
                                                decoding="async"
                                                data-nimg={1}
                                                style={{ color: "transparent" }}
                                                src={LIBERTY_IMAGES.IC_ARROW_UP_RIGHT_1}
                                                data-savepage-loading="lazy"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-4" />
                            </div>
                        </div>
                    </div>
                </div>
                {/*$*/}
                {/*/$*/}
            </main>
            <TokenSelector
                full={false}
                open={tokenSelectorOpen}
                listMode={tokenPickerField === 'pay' ? 'default' : 'pulse'}
                onClose={() => setTokenSelectorOpen(false)}
                onSelect={(sel) => {
                    const next = { ...sel }
                    if (tokenPickerField === 'pay') {
                        setPayToken(next)
                    } else {
                        setReceiveToken(next)
                    }
                }}
            />
        </>
    )
}

export default X